import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Badge } from "primereact/badge";
import { OverlayPanel } from "primereact/overlaypanel";
import { useNavigate } from "react-router-dom";
import useAccessToken from "../Custom/useAccessToken.tsx";
import {
  getUnreadNotificationCount,
  listNotifications,
  markAllNotificationsRead,
  setNotificationReadState,
  type AppNotification,
} from "../Services/notificationService.ts";
import { MISSING_NOTIFICATION_TARGET, notificationPath } from "../Utils/notificationTarget.ts";
import "./Notifications.css";

const PAGE_SIZE = 50;
const POLL_MS = 60_000;

const ICONS: Record<string, string> = {
  bell: '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>',
  assign: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6"/><path d="M22 11h-6"/>',
  unassign: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 11h-6"/>',
  status: '<path d="M8 3 4 7l4 4"/><path d="M4 7h16"/><path d="m16 21 4-4-4-4"/><path d="M20 17H4"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  unblock: '<rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/>',
  clock: '<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>',
  alert: '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
  request: '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 13h4"/><path d="M10 17h4"/>',
  layers: '<path d="m12 2 10 5-10 5L2 7Z"/><path d="m2 17 10 5 10-5"/><path d="m2 12 10 5 10-5"/>',
  mailOpen: '<path d="M21.2 8.4c.5.38.8.97.8 1.6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V10a2 2 0 0 1 .8-1.6l8-6a2 2 0 0 1 2.4 0l8 6Z"/><path d="m22 10-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 10"/>',
  mail: '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>',
};

function iconKind(type: string) {
  switch (type) {
    case "TaskAssigned":
    case "ProcessAssigned":
      return "assign";
    case "TaskUnassigned":
    case "ProcessUnassigned":
    case "RequestUnassigned":
      return "unassign";
    case "TaskStatusChanged":
    case "ProcessStatusChanged":
      return "status";
    case "TaskCompleted":
      return "check";
    case "TaskUnblocked":
      return "unblock";
    case "WorkflowTasksAssigned":
      return "layers";
    case "RequestAssigned":
    case "RequestStageChanged":
      return "request";
    case "TaskDueSoon":
      return "clock";
    case "TaskOverdue":
      return "alert";
    default:
      return "status";
  }
}

function Icon({ name, size = 18 }: { name: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: ICONS[name] }}
    />
  );
}

function dayGroup(createdAt: string) {
  const created = moment(createdAt);
  if (created.isSame(moment(), "day")) return "Today";
  if (created.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return created.format("MMM D");
}

function metaLine(item: AppNotification) {
  const when = moment(item.createdAt).fromNow();
  if (item.type === "TaskDueSoon" || item.type === "TaskOverdue") {
    return `Reminder · ${when}`;
  }
  return item.actorDisplayName ? `${item.actorDisplayName} · ${when}` : when;
}

export default function NotificationBell() {
  const { accessToken } = useAccessToken();
  const navigate = useNavigate();
  const panelRef = useRef<OverlayPanel>(null);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const refreshCount = useCallback(async () => {
    if (!accessToken || document.visibilityState !== "visible") return;
    try {
      setUnread(await getUnreadNotificationCount(accessToken));
    } catch {
      // The badge stays at the last count. Opening the panel shows the error.
    }
  }, [accessToken]);

  useEffect(() => {
    void refreshCount();
    const timer = window.setInterval(() => {
      void refreshCount();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void refreshCount();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshCount]);

  const closePanel = () => {
    panelRef.current?.hide();
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      // PrimeReact checks the physical code. Accept the semantic key as well,
      // including input sources that do not populate KeyboardEvent.code.
      if (event.key === "Escape") {
        panelRef.current?.hide();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const loadPage = useCallback(async (before?: string, beforeId?: string) => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const page = await listNotifications(accessToken, PAGE_SIZE, before, beforeId);
      setItems((current) => (before ? [...current, ...page] : page));
      setHasMore(page.length === PAGE_SIZE);
    } catch {
      if (!before) setItems([]);
      setError("Couldn't load notifications");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const openPanel = (event: React.MouseEvent<HTMLButtonElement>) => {
    panelRef.current?.show(event, event.currentTarget);
    setNotice(null);
    setOpen(true);
    void loadPage();
    void refreshCount();
  };

  const groups = useMemo(() => {
    const order: string[] = [];
    const byGroup = new Map<string, AppNotification[]>();
    items.forEach((item) => {
      const group = dayGroup(item.createdAt);
      if (!byGroup.has(group)) {
        byGroup.set(group, []);
        order.push(group);
      }
      byGroup.get(group)!.push(item);
    });
    return order.map((group) => ({ group, items: byGroup.get(group)! }));
  }, [items]);

  const updateItem = (id: string, isRead: boolean) => {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, isRead } : item)));
    setUnread((count) => Math.max(0, count + (isRead ? -1 : 1)));
  };

  const openItem = async (item: AppNotification) => {
    if (!item.isRead) {
      try {
        await setNotificationReadState(accessToken, item.id, true);
        updateItem(item.id, true);
      } catch {
        setNotice("Couldn't update that notification.");
        return;
      }
    }

    const path = notificationPath(item);
    if (!path) {
      setNotice(MISSING_NOTIFICATION_TARGET);
      return;
    }
    setNotice(null);
    closePanel();
    navigate(path);
  };

  const toggleRead = async (item: AppNotification) => {
    const next = !item.isRead;
    try {
      await setNotificationReadState(accessToken, item.id, next);
      updateItem(item.id, next);
    } catch {
      setNotice("Couldn't update that notification.");
    }
  };

  const markAll = async () => {
    try {
      await markAllNotificationsRead(accessToken);
      setItems((current) => current.map((item) => ({ ...item, isRead: true })));
      setUnread(0);
    } catch {
      setNotice("Couldn't mark notifications read.");
    }
  };

  const badge = unread > 99 ? "99+" : unread > 0 ? String(unread) : "";

  if (!accessToken) return null;

  return (
    <div className="notification-anchor">
      <button
        type="button"
        className="notification-bell"
        aria-expanded={open}
        aria-controls="notification-panel"
        aria-label={`Notifications, ${unread > 99 ? "more than 99" : unread} unread`}
        onClick={(event) => (open ? closePanel() : openPanel(event))}
      >
        <Icon name="bell" size={22} />
        {badge && <Badge value={badge} className={`notification-badge${unread > 99 ? " wide" : ""}`} />}
      </button>
      <OverlayPanel ref={panelRef} className="notification-overlay"
        dismissable closeOnEscape onHide={() => setOpen(false)}
        transitionOptions={{ timeout: 0 }}>
        <section className="notification-panel" id="notification-panel" aria-label="Notifications">
          <div className="notification-panel-head">
            <div className="flex align-items-center gap-2">
              <h3>Notifications</h3>
              {unread > 0 && <span className="notification-chip">{unread} new</span>}
            </div>
            <button type="button" className="notification-link-btn" disabled={unread === 0} onClick={() => void markAll()}>
              Mark all as read
            </button>
          </div>
          <div className="notification-list">
            {loading && items.length === 0 && [0, 1, 2].map((row) => (
              <div className="notification-skel" key={row}>
                <span className="c" />
                <span className="l"><span /><span /><span /></span>
              </div>
            ))}
            {error && (
              <div className="notification-center">
                <p style={{ fontSize: 16, fontWeight: 600 }}>{error}</p>
                <p style={{ fontSize: 13, color: "#475569" }}>Check your connection and try again.</p>
                <button type="button" className="notification-link-btn" onClick={() => void loadPage()}>Retry</button>
              </div>
            )}
            {!loading && !error && items.length === 0 && (
              <div className="notification-center">
                <p style={{ fontSize: 16, fontWeight: 600 }}>You're all caught up</p>
                <p style={{ fontSize: 13, color: "#475569", maxWidth: 260 }}>
                  Assignments, status changes and due-date reminders will show up here.
                </p>
              </div>
            )}
            {groups.map(({ group, items: groupItems }) => (
              <div key={group}>
                <div className="notification-group">{group}</div>
                {groupItems.map((item) => (
                  <div className={`notification-row${item.isRead ? "" : " unread"}`} key={item.id}>
                    <button type="button" className="notification-item" onClick={() => void openItem(item)}>
                      <span className={`notification-icon k-${iconKind(item.type)}`}>
                        <Icon name={iconKind(item.type)} />
                      </span>
                      <span className="notification-text">
                        <span className="notification-title">{item.title}</span>
                        <span className="notification-msg">{item.message}</span>
                        <span className="notification-meta">{metaLine(item)}</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="notification-toggle"
                      aria-label={`${item.isRead ? "Mark as unread" : "Mark as read"}: ${item.title}`}
                      title={item.isRead ? "Mark as unread" : "Mark as read"}
                      onClick={() => void toggleRead(item)}
                    >
                      {!item.isRead && <span className="notification-dot dot" aria-hidden="true" />}
                      <span className="hover-icon">
                        <Icon name={item.isRead ? "mail" : "mailOpen"} />
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
          {hasMore && (
            <div className="notification-foot">
              <button
                type="button"
                className="notification-link-btn"
                disabled={loading}
                onClick={() => void loadPage(items[items.length - 1]?.createdAt, items[items.length - 1]?.id)}
              >
                Load more
              </button>
            </div>
          )}
          {notice && (
            <div className="notification-notice" role="status">
              <p className="notification-meta">{notice}</p>
              <button type="button" className="notification-link-btn" onClick={() => setNotice(null)}>
                Dismiss
              </button>
            </div>
          )}
        </section>
      </OverlayPanel>
    </div>
  );
}
